import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  PlayArrow as ApproveIcon,
  Close as CancelIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

import { PreviewApprovedView } from "../components/Distribution/PreviewApprovedView";
import { PreviewStoreTable } from "../components/Distribution/PreviewStoreTable";
import { usePageTitle } from "../context/PageTitleContext";
import {
  useApprovePreview,
  useCancelPreview,
  usePreview,
  useRepromoteDeferred,
} from "../hooks/useDistributionPreview";
import type { ApproveResponse } from "../services/distributionPreviewService";

function fmtCountdown(ms: number): string {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function fmtMoney(value: number): string {
  return value.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });
}

/**
 * Página de revisión previa antes de crear stock.picking en Odoo 18
 * producción. Plan 2026_04_25_plan_distribucion_preview §5.3.
 *
 * Flujo:
 *   1. Usuario llega tras click en DistribucionTiendaPage.
 *   2. Lee la tabla por tienda; toggle checkbox para excluir líneas.
 *   3. Aprobar → confirmDialog → POST approve con exclusiones → ver
 *      resultado y enlace a transferencias.
 *      Cancelar → POST cancel → vuelve a /compras/distribucion/transferencias.
 *
 * Estados especiales:
 *   - is_expired=true → banner con botón "Generar preview nueva".
 *   - status='approved' → vista de resultado (pickings creados).
 */
export function DistribucionPreviewPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const preview = usePreview(id);
  const approve = useApprovePreview();
  const cancel = useCancelPreview();
  const repromote = useRepromoteDeferred();

  const [excludedSet, setExcludedSet] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approveResult, setApproveResult] = useState<ApproveResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [deferredOpen, setDeferredOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setTitle("Distribución — Revisión previa");
    return () => setTitle("");
  }, [setTitle]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const data = preview.data;
  const expiresAt = data ? new Date(data.session.expires_at).getTime() : 0;
  const remaining = expiresAt - now;
  const isExpired = !!data?.is_expired || remaining <= 0;

  const totals = useMemo(() => {
    if (!data) return { lines: 0, units: 0, value: 0 };
    let lines = 0;
    let units = 0;
    let value = 0;
    for (const block of data.enriched_stores) {
      for (const l of block.lines) {
        if (excludedSet.has(l.recommendation_id)) continue;
        lines += 1;
        units += l.qty_planned;
        value += l.deficit_value;
      }
    }
    return { lines, units, value };
  }, [data, excludedSet]);

  const totalLinesAvailable = useMemo(() => {
    if (!data) return 0;
    return data.enriched_stores.reduce((acc, b) => acc + b.lines.length, 0);
  }, [data]);

  function toggleLine(recId: string): void {
    setExcludedSet((prev) => {
      const next = new Set(prev);
      if (next.has(recId)) next.delete(recId);
      else next.add(recId);
      return next;
    });
  }

  async function handleApprove(): Promise<void> {
    if (!id) return;
    setConfirmOpen(false);
    setError(null);
    try {
      const params: { id: string; excluded_recommendation_ids?: string[] } = {
        id,
      };
      if (excludedSet.size > 0) {
        params.excluded_recommendation_ids = Array.from(excludedSet);
      }
      const res = await approve.mutateAsync(params);
      setApproveResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleCancel(): Promise<void> {
    if (!id) return;
    setError(null);
    try {
      await cancel.mutateAsync(id);
      navigate("/compras/distribucion/transferencias");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (preview.isLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (preview.isError || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          No se pudo cargar la revisión previa.{" "}
          <Button onClick={() => navigate("/compras/distribucion")}>
            Volver
          </Button>
        </Alert>
      </Box>
    );
  }

  if (approveResult) {
    return <PreviewApprovedView result={approveResult} navigate={navigate} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" component="h1" fontWeight={600}>
            Revisión previa
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TimeIcon color={isExpired ? "error" : "primary"} />
            <Typography
              variant="h6"
              color={isExpired ? "error" : "text.primary"}
              sx={{ fontFamily: "monospace" }}
            >
              {isExpired ? "EXPIRADA" : fmtCountdown(remaining)}
            </Typography>
          </Stack>
        </Stack>

        {isExpired && (
          <Alert
            severity="error"
            icon={<WarningIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate("/compras/distribucion")}
              >
                Generar nueva preview
              </Button>
            }
          >
            La revisión previa expiró (30 min TTL). Genera una nueva para ver
            el cálculo actualizado.
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <SummaryStat label="Tiendas" value={data.enriched_stores.length} />
            <SummaryStat
              label="Líneas seleccionadas"
              value={totals.lines}
              sub={`de ${totalLinesAvailable}`}
            />
            <SummaryStat label="Unidades" value={totals.units} />
            <SummaryStat label="Valor" valueText={fmtMoney(totals.value)} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                request_id
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
              >
                {data.session.request_id}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {data.enriched_stores.map((block) => (
          <PreviewStoreTable
            key={block.store_mxp_code}
            block={block}
            excludedSet={excludedSet}
            onToggle={toggleLine}
            deferredOpen={!!deferredOpen[block.store_mxp_code]}
            onToggleDeferred={() =>
              setDeferredOpen((prev) => ({
                ...prev,
                [block.store_mxp_code]: !prev[block.store_mxp_code],
              }))
            }
            onRepromoteDeferred={async (ids) => {
              if (!id) return;
              await repromote.mutateAsync({
                id,
                deferred_recommendation_ids: ids,
              });
            }}
          />
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            position: "sticky",
            bottom: 0,
            py: 2,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CancelIcon />}
            disabled={cancel.isPending || approve.isPending}
            onClick={() => void handleCancel()}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              approve.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ApproveIcon />
              )
            }
            disabled={
              isExpired ||
              approve.isPending ||
              cancel.isPending ||
              totals.lines === 0
            }
            onClick={() => setConfirmOpen(true)}
          >
            Aprobar y crear en Odoo ({totals.lines})
          </Button>
        </Box>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar creación en Odoo 18</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se crearán <strong>{totals.lines}</strong> líneas en Odoo 18
            producción.{" "}
            {excludedSet.size > 0 && (
              <>
                <strong>{excludedSet.size}</strong> líneas excluidas vuelven a{" "}
                <em>pending</em> para próxima corrida.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleApprove()}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SummaryStat({
  label,
  value,
  valueText,
  sub,
}: {
  label: string;
  value?: number;
  valueText?: string;
  sub?: string;
}): React.ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600}>
        {valueText ?? value?.toLocaleString("es-MX") ?? "—"}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  );
}
